import React, { useEffect, useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import {
  Button,
  Grid,
  Input,
  Paper,
  TextField,
  Typography,
  withStyles,
} from "@material-ui/core";
import MuiAccordionSummary from "@material-ui/core/AccordionSummary";
import MuiAccordion from "@material-ui/core/Accordion";
import MuiAccordionDetails from "@material-ui/core/AccordionDetails";
import Autocomplete from "@material-ui/lab/Autocomplete";
import countries from "i18n-iso-countries";
import timezones from "timezones-list";
import {
  DefaultRegionCode,
  NAME_CHARACTER_LIMIT,
  PhoneTypes,
  ReducerStates,
  sortAlphabetical,
  SupportedLanguages,
  Temperature,
  TimeFormat,
} from "../../helpers/constants";
import { useAppSelector } from "../app/appHooks";
import {
  IPhoneNumber,
  IResidentPhoneNumber,
  IUserDetails,
} from "../../services/header.services";
import { useDispatch } from "react-redux";
import {
  IPasswordChangeData,
  IProfileData,
} from "../../services/settings.services";
import { submitMyProfileChanges } from "../app/asyncThunkActions";
import { ExpandMoreOutlined } from "@material-ui/icons";
import { ErrorCodes } from "../../services/constants";
import { passwordValidation } from "../../helpers/utils";
import { changePassword, resetSliceState } from "../login/loginSlice";
import { PhoneNumberPicker, UnitPhoneNumber } from "./common/PhoneNumberPicker";
import {
  createUserPhoneNumber,
  deleteUserPhoneNumber,
  IAddUserPhoneNumber,
  updateUserPhoneNumber,
} from "./settingsThunks";
import {
  adjustPhoneNumber,
  getPhoneOperationsArr,
  phoneOperations,
} from "./common/phoneHelpers";
import { getRegionCodeForCountryCode } from "awesome-phonenumber";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    myProfileContainer: {
      padding: theme.spacing(6, 7),
      width: "550px",
      overflow: "auto",
      maxHeight: "100vh",
    },
    formContainer: {
      display: "flex",
      flexDirection: "column",
      marginBottom: theme.spacing(4),
    },
    sectionTitle: {
      marginTop: 0,
      marginBottom: theme.spacing(3),
      textAlign: "center",
    },
    inputControl: {
      display: "flex",
      alignItems: "center",
    },
    wrapper: {
      listStyle: "none",
      padding: 0,
      borderRadius: "3px",
    },
    formRow: {
      display: "flex",
      justifyContent: "flex-end",
      padding: ".5em",
      "& > label": {
        padding: ".5em 1em .5em 0",
        flex: " 1",
        textAlign: "right",
        color: theme.palette.paused.main,
      },
    },
    rowInput: {
      flex: "1.5",
    },
    buttonRoot: {
      fontFamily: theme.typography.secondaryFontFamily,
      fontSize: "1.1rem !important",
      minWidth: "120px",
    },
    flagIcon: {
      marginRight: theme.spacing(0.5),
    },
    buttonContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      paddingTop: theme.spacing(4),
    },
    subheaderRoot: {
      lineHeight: "16px",
    },
    changePasswordButtonContainer: {
      display: "flex",
      justifyContent: "flex-end",
      gap: theme.spacing(1),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    gridContainer: {
      alignItems: "center",
      gap: theme.spacing(2),
    },
    changePasswordLabel: {
      textAlign: "right",
    },
    passwordText: {
      fontSize: "0.80rem",
      paddingBottom: "6px",
    },
    changePasswordBox: {
      display: "block",
      padding: "0",
      marginTop: theme.spacing(2),
    },
    textField: {
      minHeight: "52px",
    },
    helperText: {
      fontSize: "0.60rem",
    },
    accordionHeader: {
      borderRadius: "0",
    },
    accordionHeaderText: {
      fontWeight: "bold",
    },
    phoneNumbersTitle: {
      marginTop: theme.spacing(7),
      marginLeft: theme.spacing(8),
    },
    phoneNumbersContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
    },
  })
);

const Accordion = withStyles({
  root: {
    "&:before": {
      display: "none",
    },
    "&$expanded": {
      margin: 0,
      marginLeft: 2,
      marginRight: 2,
      height: "fit-content",
      marginBottom: 20,
    },
    border: "1px solid #ccc",
    borderRadius: "5px",
    fontWeight: "bold",
    marginBottom: 20,
    marginLeft: 2,
    marginRight: 2,
    boxShadow: "none",
  },
  expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles({
  root: {
    minHeight: 60,
    "&$expanded": {
      minHeight: 60,
    },
    alignItems: "baseline",
  },
  content: {
    "&$expanded": {
      margin: "12px 0",
    },
  },
  expanded: {},
})(MuiAccordionSummary);

const AccordionDetails = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    paddingTop: 0,
    borderBottom: "1px solid #ccc",
  },
}))(MuiAccordionDetails);

// ISO 3166-1 alpha-2
// ⚠️ No support for IE 11
function countryToFlag(isoCode: string) {
  return typeof String.fromCodePoint !== "undefined"
    ? isoCode
        .toUpperCase()
        .replace(/./g, (char) =>
          String.fromCodePoint(char.charCodeAt(0) + 127397)
        )
    : isoCode;
}

enum FormFieldIDs {
  phoneNumberOne = "phone-number-one",
  phoneNumberTwo = "phone-number-two",
}

export type UnitPhoneNumberUpdate = UnitPhoneNumber & { id?: string | null };

interface OptionsType {
  id: string;
  label: string;
  groupBy?: string;
}

const InputRow = (props: {
  inputKey: string;
  value: string | OptionsType | null;
  select?: boolean;
  required?: boolean;
  options?: OptionsType[];
  onChange?: (
    key: string
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;

  onChangeSelect?: (key: string, value: OptionsType | null) => void;
}) => {
  const {
    inputKey,
    value,
    select,
    options,
    required,
    onChange,
    onChangeSelect,
  } = props;
  /* Hooks */
  const classes = useStyles();

  const { t } = useTranslation();
  const hasGroupBy = options?.every((option) => option.groupBy);
  const inputProps: { [key: string]: any } = {};
  if (inputKey === "first_name" || inputKey === "last_name") {
    inputProps["maxLength"] = NAME_CHARACTER_LIMIT;
  }
  return (
    <li className={classes.formRow}>
      <label htmlFor={inputKey}>{`${t(inputKey).toUpperCase()}${
        required ? "*" : ""
      }`}</label>
      {select ? (
        <Autocomplete
          options={options ?? []}
          id={`dropdown-${inputKey}`}
          disableClearable
          noOptionsText={t("no_options")}
          groupBy={hasGroupBy ? (option) => option.groupBy ?? "" : undefined}
          getOptionLabel={(option: OptionsType) =>
            options?.find((_option) => _option.id === option.id)?.label ?? ""
          }
          value={value as OptionsType | undefined}
          onChange={(_, val) => onChangeSelect && onChangeSelect(inputKey, val)}
          getOptionSelected={(option, value) => option.id === value.id}
          blurOnSelect
          autoHighlight
          renderOption={(option) => (
            <React.Fragment>
              {inputKey === "country" && (
                <span className={classes.flagIcon}>
                  {countryToFlag(option.id)}
                </span>
              )}
              {option.label}
            </React.Fragment>
          )}
          classes={{
            root: classes.rowInput,
            groupLabel:
              inputKey === "country" ? classes.subheaderRoot : undefined,
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={value === null ? t("select_an_option") : ""}
            />
          )}
        />
      ) : (
        <Input
          classes={{ root: classes.rowInput }}
          id={inputKey}
          readOnly={inputKey === "email"}
          disableUnderline={inputKey === "email"}
          error={required ? value === null || value === "" : false}
          value={value !== null ? (value as string) : ""}
          onChange={onChange && onChange(inputKey)}
          inputProps={inputProps}
        />
      )}
    </li>
  );
};

interface FormState {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  country: OptionsType | null;
  language: OptionsType | null;
  time_zone: OptionsType | null;
  temperature_unit: OptionsType | null;
  is_twenty_four_time: OptionsType | null;
}

type PhoneNumberFormState = {
  [FormFieldIDs.phoneNumberOne]: UnitPhoneNumberUpdate;
  [FormFieldIDs.phoneNumberTwo]: UnitPhoneNumberUpdate;
};

export const initializePhoneValues = (
  phone_numbers: IPhoneNumber[],
  defaultRegionCode: string
): PhoneNumberFormState => {
  const initialVals: PhoneNumberFormState = {
    [FormFieldIDs.phoneNumberOne]: {
      id: null,
      country_code: defaultRegionCode,
      phone_type: PhoneTypes.HOME,
      phone_number: null,
    },
    [FormFieldIDs.phoneNumberTwo]: {
      id: null,
      country_code: defaultRegionCode,
      phone_type: PhoneTypes.MOBILE,
      phone_number: null,
    },
  };

  if (phone_numbers && phone_numbers.length > 0) {
    const phoneNumbersCopy = JSON.parse(JSON.stringify(phone_numbers));
    phoneNumbersCopy.sort(
      <T extends { priority?: number | null }>(a: T, b: T) =>
        Number(a.priority) - Number(b.priority)
    );

    if (phoneNumbersCopy.length > 0) {
      const phoneNumberOne = phoneNumbersCopy[0];
      const countryCode = phoneNumberOne.country_code
        ? String(
            getRegionCodeForCountryCode(Number(phoneNumberOne.country_code))
          )
        : defaultRegionCode;
      initialVals[FormFieldIDs.phoneNumberOne] = {
        id: phoneNumberOne.id,
        country_code: countryCode,
        phone_type: phoneNumberOne.phone_type ?? PhoneTypes.HOME,
        phone_number: phoneNumberOne.phone_number ?? null,
      };
    }
    if (phoneNumbersCopy.length > 1) {
      const phoneNumberTwo = phoneNumbersCopy[1];
      const countryCode = phoneNumberTwo.country_code
        ? String(
            getRegionCodeForCountryCode(Number(phoneNumberTwo.country_code))
          )
        : defaultRegionCode;
      initialVals[FormFieldIDs.phoneNumberTwo] = {
        id: phoneNumberTwo.id,
        country_code: countryCode,
        phone_type: phoneNumberTwo.phone_type ?? PhoneTypes.HOME,
        phone_number: phoneNumberTwo.phone_number ?? null,
      };
    }
  }

  return initialVals;
};

export const checkPhoneNumbersChanged = (
  oldData: IPhoneNumber[] | IResidentPhoneNumber[] | null | undefined,
  values: PhoneNumberFormState,
  userType: string
) => {
  let changed = false;
  if (oldData === undefined) return changed;
  const phone1 = values[FormFieldIDs.phoneNumberOne];
  if (phone1.id) {
    if (phone1.phone_number) {
      const oldPhone = oldData?.find((oldPhone) => oldPhone.id === phone1.id);
      const adjustedPhone1 = adjustPhoneNumber(phone1, userType);
      if (
        oldPhone &&
        (oldPhone.phone_number !== adjustedPhone1.phone_number ||
          oldPhone.phone_type !== adjustedPhone1.phone_type ||
          oldPhone.country_code !== adjustedPhone1.country_code)
      ) {
        changed = true;
      }
    } else {
      changed = true;
    }
  } else {
    if (phone1.phone_number) {
      changed = true;
    }
  }
  const phone2 = values[FormFieldIDs.phoneNumberTwo];
  if (phone2.id) {
    if (phone2.phone_number) {
      const oldPhone = oldData?.find((oldPhone) => oldPhone.id === phone2.id);
      const adjustedPhone2 = adjustPhoneNumber(phone2, userType);
      if (
        oldPhone &&
        (oldPhone.phone_number !== adjustedPhone2.phone_number ||
          oldPhone.phone_type !== adjustedPhone2.phone_type ||
          oldPhone.country_code !== adjustedPhone2.country_code)
      ) {
        changed = true;
      }
    } else {
      changed = true;
    }
  } else {
    if (phone2.phone_number) {
      changed = true;
    }
  }
  return changed;
};

export function MyProfile() {
  /* Hooks */
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [countryListFormmatted, setCountryListFormatted] = useState<
    OptionsType[]
  >([]);
  const [supportedLanguages, setSupportedLanguages] = useState<OptionsType[]>(
    []
  );
  const [temperature, setTemperature] = useState<OptionsType[]>([]);
  const [timeFormat, setTimeFormat] = useState<OptionsType[]>([]);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [codeError, setCodeError] = useState("");

  /* Selectors */
  const user = useAppSelector((state) => state.headerState.user);
  const errorCode = useAppSelector((state) => state.loginState.errorCode);
  const email = useAppSelector((state) => state.headerState.user?.email);
  const loginState = useAppSelector((state) => state.loginState.state);

  const defaultRegionCode = useAppSelector(
    (state) => state.headerState.user?.country ?? DefaultRegionCode
  );

  const timezoneFormated: OptionsType[] = timezones.map((timezone) => ({
    id: timezone.tzCode,
    label: `${timezone.tzCode}`,
    groupBy: `GMT${timezone.utc}`,
  }));

  /* State */
  const [profileValues, setProfileValues] = useState<FormState>({
    first_name: null,
    last_name: null,
    email: null,
    country: null,
    language: null,
    time_zone: null,
    temperature_unit: null,
    is_twenty_four_time: null,
  });

  const [formValues, setFormValues] = useState<PhoneNumberFormState>({
    [FormFieldIDs.phoneNumberOne]: {
      id: null,
      country_code: defaultRegionCode,
      phone_type: PhoneTypes.HOME,
      phone_number: null,
    },
    [FormFieldIDs.phoneNumberTwo]: {
      id: null,
      country_code: defaultRegionCode,
      phone_type: PhoneTypes.MOBILE,
      phone_number: null,
    },
  });

  const getNewPhoneNumber = (
    phone: UnitPhoneNumberUpdate,
    user_id: string
  ): IAddUserPhoneNumber["phoneNumber"] => {
    delete phone.id;
    return {
      ...adjustPhoneNumber(phone, "user"),
      user_id: user_id,
    };
  };

  const getUpdatedPhoneNumber = (
    phone: UnitPhoneNumberUpdate,
    oldPhoneNumbers: IPhoneNumber[] | null | undefined
  ): IPhoneNumber => {
    const oldPhone = oldPhoneNumbers?.find(
      (oldPhone) => phone.id === oldPhone.id
    );
    if (oldPhone) {
      const updatedPhoneNumber: { [key: string]: any } = {
        ...oldPhone,
        ...adjustPhoneNumber(phone, "user"),
        id: phone.id as string,
      };

      Object.keys(oldPhone).forEach((key) => {
        if (updatedPhoneNumber[key] === null) {
          delete updatedPhoneNumber[key];
        }
      });
      return updatedPhoneNumber as IPhoneNumber;
    } else {
      return {
        ...adjustPhoneNumber(phone, "user"),
        id: phone.id as string,
      };
    }
  };

  /* Hooks */
  useEffect(() => {
    if (user !== undefined) {
      let countryOption: OptionsType | null = null;
      if (user.country) {
        countryOption =
          countryListFormmatted.find(
            (countryOption) => countryOption.id === user.country
          ) ?? null;
      }

      let languageOption: OptionsType | null = null;
      if (user.language) {
        languageOption =
          supportedLanguages.find(
            (languageOption) => languageOption.id === user.language
          ) ?? null;
      }

      let temperatureUnitOption: OptionsType | null = null;
      if (user.temperature_unit !== null) {
        temperatureUnitOption =
          temperature.find(
            (temperature_unit) => temperature_unit.id === user.temperature_unit
          ) ?? null;
      }
      let timezoneOption: OptionsType | null = null;
      if (user.time_zone !== null) {
        timezoneOption =
          timezoneFormated.find(
            (time_zone) => time_zone.id === user.time_zone
          ) ?? null;
      }

      let twentyFourOption: OptionsType | null = null;
      if (user.is_twenty_four_time !== null) {
        const option = user.is_twenty_four_time
          ? TimeFormat.TIME24H
          : TimeFormat.TIME12H;
        twentyFourOption =
          timeFormat.find((timeFormat) => timeFormat.id === option) ?? null;
      }

      setProfileValues({
        ...profileValues,
        first_name: user.first_name ?? null,
        last_name: user.last_name ?? null,
        email: user.email,
        country: countryOption,
        language: languageOption,
        time_zone: timezoneOption,
        temperature_unit: temperatureUnitOption,
        is_twenty_four_time: twentyFourOption,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, countryListFormmatted]);

  useEffect(() => {
    if (user?.phone_numbers) {
      // Always set both phone slots so deleted numbers are cleared
      setFormValues(
        initializePhoneValues(user.phone_numbers, defaultRegionCode)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, user?.phone_numbers]);

  useEffect(() => {
    if (ErrorCodes.hasOwnProperty(errorCode)) {
      switch (ErrorCodes[errorCode]) {
        case ErrorCodes.INVALID_EMAIL_PASSWORD:
          setCodeError(t("current_password_is_incorrect"));
          break;
        default:
          setCodeError("");
          break;
      }
    }
  }, [errorCode, t, dispatch]);

  useEffect(() => {
    const loadLanguage =
      countries.langs().find((item) => item === i18n.language) ?? "en";

    const countryList = countries.getNames(loadLanguage, {
      select: "official",
    });

    const countryListFormated: OptionsType[] = Object.keys(countryList).map(
      (_key) => ({
        id: _key,
        label: countryList[_key],
        groupBy: "\u00a0",
      })
    );

    const extra = countryListFormated
      .filter(
        (country) =>
          country.id === "US" || country.id === "GB" || country.id === "CA"
      )
      .map((_option) => ({ ..._option, groupBy: "\u2000" }))
      .sort((a, b) => sortAlphabetical(a.label, b.label));

    countryListFormated.unshift(...extra);

    const supportedLanguages = SupportedLanguages.map((lang) => ({
      id: lang.id,
      label: t(lang.label),
    })).sort((a, b) => sortAlphabetical(a.label, b.label));

    const temperature = [
      { id: Temperature.CELSIUS, label: "Celsius (°C)" },
      { id: Temperature.FAHRENHEIT, label: "Fahrenheit (°F)" },
    ].sort((a, b) => sortAlphabetical(a.label, b.label));
    const timeFormat = [
      { id: TimeFormat.TIME24H, label: t("24h_label") },
      { id: TimeFormat.TIME12H, label: t("12h_label") },
    ].sort((a, b) => sortAlphabetical(a.label, b.label));

    setCountryListFormatted(countryListFormated);
    setSupportedLanguages(supportedLanguages);
    setTemperature(temperature);
    setTimeFormat(timeFormat);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    if (ReducerStates[loginState] === ReducerStates.SUCCEEDED) {
      setExpanded(false);
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setCodeError("");
      setNewPasswordError("");
      setConfirmPasswordError("");
      dispatch(resetSliceState);
    }
  }, [dispatch, loginState]);

  const checkFormChange = (newVals: FormState, oldVals?: IUserDetails) => {
    if (oldVals === undefined) return false;

    let formChanged = false;

    if (
      oldVals.first_name !== null &&
      newVals.first_name !== null &&
      oldVals.first_name !== newVals.first_name
    ) {
      formChanged = true;
    }

    if (
      oldVals.last_name !== null &&
      newVals.last_name !== null &&
      oldVals.last_name !== newVals.last_name
    ) {
      formChanged = true;
    }

    if (oldVals.country === null && newVals.country?.id !== null) {
      formChanged = true;
    }
    if (newVals.country !== null && oldVals.country !== newVals.country?.id) {
      formChanged = true;
    }
    if (oldVals.language === null && newVals.language !== null) {
      formChanged = true;
    }
    if (
      newVals.language !== null &&
      oldVals.language !== newVals.language?.id
    ) {
      formChanged = true;
    }
    if (oldVals.time_zone === null && newVals.time_zone !== null) {
      formChanged = true;
    }
    if (
      newVals.time_zone !== null &&
      oldVals.time_zone !== newVals.time_zone?.id
    ) {
      formChanged = true;
    }
    if (
      oldVals.temperature_unit === null &&
      newVals.temperature_unit !== null
    ) {
      formChanged = true;
    }
    if (
      newVals.temperature_unit !== null &&
      oldVals.temperature_unit !== newVals.temperature_unit?.id
    ) {
      formChanged = true;
    }
    const twentyFourOptionOld =
      oldVals.is_twenty_four_time !== null
        ? oldVals.is_twenty_four_time
          ? TimeFormat.TIME24H
          : TimeFormat.TIME12H
        : null;
    const twentyFourOptionNew =
      newVals.is_twenty_four_time !== null
        ? newVals.is_twenty_four_time.id
        : null;

    if (twentyFourOptionOld === null && twentyFourOptionNew !== null) {
      formChanged = true;
    }
    if (
      twentyFourOptionNew !== null &&
      twentyFourOptionOld !== twentyFourOptionNew
    ) {
      formChanged = true;
    }

    return formChanged;
  };

  const checkNullValues = (newVals: FormState, oldVals?: IUserDetails) => {
    if (oldVals === undefined) return false;

    let nullValues = false;

    if (
      oldVals.first_name !== null &&
      (newVals.first_name === null ||
        newVals.first_name === "" ||
        newVals.first_name.trim() === "")
    ) {
      nullValues = true;
    }

    if (
      oldVals.last_name !== null &&
      (newVals.last_name === null ||
        newVals.last_name === "" ||
        newVals.last_name.trim() === "")
    ) {
      nullValues = true;
    }

    if (oldVals.country !== null && newVals.country === null) {
      nullValues = true;
    }

    const oldLanguageLocal =
      supportedLanguages.find(
        (languageOption) => languageOption.id === oldVals.language
      )?.id ?? null;
    if (oldLanguageLocal !== null && newVals.language === null) {
      nullValues = true;
    }
    if (oldVals.time_zone !== null && newVals.time_zone === null) {
      nullValues = true;
    }
    if (
      oldVals.temperature_unit !== null &&
      newVals.temperature_unit === null
    ) {
      nullValues = true;
    }
    const twentyFourOptionOld =
      oldVals.is_twenty_four_time !== null
        ? oldVals.is_twenty_four_time
          ? TimeFormat.TIME24H
          : TimeFormat.TIME12H
        : null;
    const twentyFourOptionNew =
      newVals.is_twenty_four_time !== null
        ? newVals.is_twenty_four_time.id
        : null;

    if (twentyFourOptionOld !== null && twentyFourOptionNew === null) {
      nullValues = true;
    }

    return nullValues;
  };

  /* Methods */

  const isTwentyFourHourOption = (val: OptionsType) =>
    String(val.id === TimeFormat.TIME24H);

  const handleChange = (key: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setProfileValues((prevState) => ({
      ...prevState,
      [key]: event.target.value.trimStart(),
    }));
  };

  const handleChangeDropdown = (key: string, value: OptionsType | null) => {
    setProfileValues((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    const myProfileData: IProfileData = {};
    if (checkFormChange(profileValues, user)) {
      Object.keys(profileValues).forEach((item) => {
        const val = profileValues[item as keyof typeof profileValues];
        if (val !== null && user) {
          if (
            typeof val === "string" &&
            val !== user[item as keyof typeof user]
          ) {
            myProfileData[item as keyof typeof myProfileData] = val;
          } else if (
            typeof val !== "string" &&
            val.id !== user[item as keyof typeof user]
          ) {
            if (item === "is_twenty_four_time") {
              myProfileData[
                item as keyof typeof myProfileData
              ] = isTwentyFourHourOption(val);
            } else {
              myProfileData[item as keyof typeof myProfileData] = val.id;
            }
          }
        }
      });
      dispatch(submitMyProfileChanges(myProfileData));
    }

    if (
      user &&
      checkPhoneNumbersChanged(user?.phone_numbers, formValues, "user")
    ) {
      handlePhoneNumberUpdate(user.phone_numbers, formValues, user.id);
    }
  };

  const handlePhoneNumberUpdate = async (
    oldPhoneNumbers: IPhoneNumber[] | null | undefined,
    newValues: PhoneNumberFormState,
    user_id: string
  ) => {
    const phoneOperation = getPhoneOperationsArr(
      oldPhoneNumbers,
      [
        newValues[FormFieldIDs.phoneNumberOne],
        newValues[FormFieldIDs.phoneNumberTwo],
      ] as const,
      "user"
    );

    for (const item of phoneOperation) {
      switch (item.op) {
        case phoneOperations.add:
          await dispatch(
            createUserPhoneNumber({
              phoneNumber: getNewPhoneNumber(item.phone, user_id),
            })
          );
          break;
        case phoneOperations.update:
          await dispatch(
            updateUserPhoneNumber({
              user_id: user_id,
              phoneNumber: getUpdatedPhoneNumber(item.phone, oldPhoneNumbers),
            })
          );
          break;
        case phoneOperations.delete:
          if (item.phone.id) {
            await dispatch(
              deleteUserPhoneNumber({
                user_id: user_id,
                phoneNumberID: item.phone.id,
              })
            );
          }
          break;
        case phoneOperations.same:
          // check if priority changed?
          await dispatch(
            updateUserPhoneNumber({
              user_id: user_id,
              phoneNumber: getUpdatedPhoneNumber(item.phone, oldPhoneNumbers),
            })
          );
          break;
        default:
          break;
      }
    }
  };

  const handleChangePhone = (
    value: UnitPhoneNumber,
    formField: FormFieldIDs
  ) => {
    setFormValues((prevVal) => ({ ...prevVal, [formField]: value }));
  };

  const handleSubmitPasswordChange = (
    e: React.FormEvent<HTMLFormElement>
  ): void => {
    e.preventDefault();
    if (email) {
      const passwordChangeData: IPasswordChangeData = {
        email,
        oldPassword,
        newPassword,
      };
      dispatch(changePassword(passwordChangeData));
    }
  };

  const validatePasswordFields = () => {
    const validationMessage = passwordValidation(newPassword);
    if (validationMessage !== "") {
      setNewPasswordError(t(validationMessage));
    } else {
      setNewPasswordError("");
    }

    const confirmValidationMessage = passwordValidation(
      newPassword,
      confirmPassword
    );
    if (
      confirmValidationMessage !== "" &&
      confirmValidationMessage !== "password_requirements"
    ) {
      setConfirmPasswordError(t(confirmValidationMessage));
    } else {
      setConfirmPasswordError("");
    }
  };

  const enableSubmit =
    (checkFormChange(profileValues, user) ||
      checkPhoneNumbersChanged(user?.phone_numbers, formValues, "user")) &&
    !checkNullValues(profileValues, user);

  const enableChangePasswordSubmit =
    !oldPassword ||
    !newPassword ||
    !confirmPassword ||
    newPasswordError !== "" ||
    confirmPasswordError !== "";

  return (
    <Paper className={classes.myProfileContainer} elevation={3} square>
      <h2 className={classes.sectionTitle}>{t("my_profile")}</h2>
      <form
        id="login-form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}
        className={classes.formContainer}
      >
        <ul className={classes.wrapper}>
          <InputRow
            inputKey="first_name"
            value={profileValues.first_name}
            required
            onChange={handleChange}
          />
          <InputRow
            inputKey="last_name"
            value={profileValues.last_name}
            required
            onChange={handleChange}
          />
          <InputRow
            inputKey="email"
            value={profileValues.email}
            onChange={handleChange}
          />
          <InputRow
            inputKey="country"
            value={profileValues.country}
            select
            options={countryListFormmatted}
            onChangeSelect={handleChangeDropdown}
          />
          <InputRow
            inputKey="language"
            value={profileValues.language}
            select
            options={supportedLanguages}
            onChangeSelect={handleChangeDropdown}
          />
          <InputRow
            inputKey="time_zone"
            value={profileValues.time_zone}
            select
            options={timezoneFormated}
            onChangeSelect={handleChangeDropdown}
          />
          <InputRow
            inputKey="temperature_unit"
            value={profileValues.temperature_unit}
            select
            options={temperature}
            onChangeSelect={handleChangeDropdown}
          />
          <InputRow
            inputKey="is_twenty_four_time"
            value={profileValues.is_twenty_four_time}
            select
            options={timeFormat}
            onChangeSelect={handleChangeDropdown}
          />
        </ul>

        <div>
          <div className={classes.phoneNumbersTitle}>
            {t("phone_numbers").toUpperCase()}
          </div>
          <div className={classes.phoneNumbersContainer}>
            <PhoneNumberPicker
              fieldID={FormFieldIDs.phoneNumberOne}
              labelComplement={"1."}
              defaultCountryCode={defaultRegionCode}
              phoneNumber={formValues[FormFieldIDs.phoneNumberOne]}
              formFull={true}
              onChange={(value: UnitPhoneNumber) =>
                handleChangePhone(value, FormFieldIDs.phoneNumberOne)
              }
            />
            <PhoneNumberPicker
              fieldID={FormFieldIDs.phoneNumberTwo}
              labelComplement={"2."}
              defaultCountryCode={defaultRegionCode}
              phoneNumber={formValues[FormFieldIDs.phoneNumberTwo]}
              formFull={true}
              onChange={(value: UnitPhoneNumber) =>
                handleChangePhone(value, FormFieldIDs.phoneNumberTwo)
              }
            />
          </div>
        </div>
        <div className={classes.buttonContainer}>
          <Button
            type="submit"
            variant={!enableSubmit ? "outlined" : "contained"}
            color="primary"
            disabled={!enableSubmit}
            classes={{
              root: classes.buttonRoot,
            }}
          >
            {t("save")}
          </Button>
        </div>
      </form>

      <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
        <AccordionSummary
          expandIcon={<ExpandMoreOutlined />}
          aria-controls="change-password-content"
          id="change-password-header"
          className={classes.accordionHeader}
        >
          <Typography className={classes.accordionHeaderText}>
            {t("change_password")}
          </Typography>
        </AccordionSummary>
        <form
          id="change-password-form"
          autoComplete="off"
          onSubmit={handleSubmitPasswordChange}
        >
          <AccordionDetails className={classes.changePasswordBox}>
            <Grid container className={classes.gridContainer}>
              <Grid item xs={5} className={classes.changePasswordLabel}>
                <Typography className={classes.passwordText}>
                  {t("current_password").toUpperCase()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  className={classes.textField}
                  type="password"
                  variant="standard"
                  fullWidth
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  onFocus={() => setCodeError("")}
                  FormHelperTextProps={{ className: classes.helperText }}
                  error={codeError !== ""}
                  helperText={codeError}
                />
              </Grid>
            </Grid>
            <Grid container className={classes.gridContainer}>
              <Grid item xs={5} className={classes.changePasswordLabel}>
                <Typography className={classes.passwordText}>
                  {t("new_password").toUpperCase()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  className={classes.textField}
                  type="password"
                  variant="standard"
                  fullWidth
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onFocus={() => setCodeError("")}
                  onBlur={validatePasswordFields}
                  FormHelperTextProps={{ className: classes.helperText }}
                  error={newPasswordError !== ""}
                  helperText={newPasswordError}
                />
              </Grid>
            </Grid>
            <Grid container className={classes.gridContainer}>
              <Grid item xs={5} className={classes.changePasswordLabel}>
                <Typography className={classes.passwordText}>
                  {t("confirm_password").toUpperCase()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  className={classes.textField}
                  type="password"
                  variant="standard"
                  fullWidth
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onFocus={() => setCodeError("")}
                  onBlur={validatePasswordFields}
                  FormHelperTextProps={{ className: classes.helperText }}
                  error={confirmPasswordError !== ""}
                  helperText={confirmPasswordError}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
          <div className={classes.changePasswordButtonContainer}>
            <Button
              onClick={() => {
                setOldPassword("");
                setNewPassword("");
                setConfirmPassword("");
                setCodeError("");
                setNewPasswordError("");
                setConfirmPasswordError("");
                setExpanded(false); // Add this line to close the accordion
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={enableChangePasswordSubmit}
            >
              {t("save")}
            </Button>
          </div>
        </form>
      </Accordion>
    </Paper>
  );
}
